import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchRinascenteOutput,
  clearRinascenteOutput,
  reopenRinascenteInvoice,
  rinascentePdfDownloadUrl,
} from "../../api/client";
import { useAuth } from "../../auth/useAuth";
import { canManageQueue } from "../../auth/permissions";
import type { RinascenteOutputItem, RinascenteOutputListResponse } from "../../api/types";
import { useToast } from "../../context/ToastContext";
import { ConfirmModal } from "../../components/ConfirmModal";

export function RinascenteQueue() {
  return (
    <div>
      <h1 className="page-title" style={{ marginBottom: "var(--space-4)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 24, marginRight: 8, verticalAlign: "middle" }}>outbox</span>
        Coda Output
      </h1>
      <QueueTab />
    </div>
  );
}

function QueueTab() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  const canWrite = canManageQueue(user?.roles ?? []);
  const [data, setData] = useState<RinascenteOutputListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(() => {
    setIsLoading(true);
    fetchRinascenteOutput(1, 100)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleClearQueue = async () => {
    setActionLoading(true);
    try {
      const res = await clearRinascenteOutput();
      const n = res.reopened?.length ?? 0;
      const errs = res.errors?.length ?? 0;
      addToast(
        errs > 0 ? "error" : "success",
        errs > 0
          ? `Coda svuotata con ${errs} errori — ${n} riaperte`
          : `Coda svuotata — ${n} fatture riaperte`,
      );
      setConfirmClear(false);
      load();
    } catch (e: unknown) {
      addToast("error", `Errore: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopen = async (id: string) => {
    setActionLoading(true);
    try {
      const res = await reopenRinascenteInvoice(id);
      const purge = res.output_purge;
      const errs = purge?.errors?.length ?? 0;
      addToast(
        errs > 0 ? "error" : "success",
        errs > 0
          ? `Riaperta con ${errs} errori`
          : `Fattura riaperta. JSON rimosso (${purge.blobs_deleted} file).`,
      );
      setConfirmDeleteId(null);
      load();
    } catch (e: unknown) {
      addToast("error", `Errore: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadJson = (item: RinascenteOutputItem) => {
    const blob = new Blob([JSON.stringify(item.final_data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rinascente_${item.numero_documento || item.id.slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = (item: RinascenteOutputItem) => {
    // Redirect al endpoint BE che restituisce il PDF inline (con token gestito dal browser)
    const token = (window as unknown as { __authToken?: string }).__authToken;
    const a = document.createElement("a");
    a.href = rinascentePdfDownloadUrl(item.id) + (token ? `?t=${encodeURIComponent(token)}` : "");
    a.download = item.pdf_filename || `rinascente_${item.id}.pdf`;
    a.target = "_blank";
    a.click();
  };

  const items = data?.items ?? [];

  return (
    <>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)", flexWrap: "wrap", rowGap: "var(--space-2)" }}>
        <span className="text-muted" style={{ fontSize: "var(--font-size-sm)", minWidth: 0 }}>
          {data ? `${data.total} fatture in coda` : "Caricamento..."}
        </span>
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          {items.length > 0 && canWrite && (
            <button className="btn btn-danger btn-sm" onClick={() => setConfirmClear(true)} disabled={actionLoading}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete_sweep</span>
              Svuota coda
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={isLoading}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>refresh</span>
            Aggiorna
          </button>
        </div>
      </div>

      {isLoading && !data && <div className="loading-center"><div className="spinner spinner-lg" /></div>}

      {error && (
        <div className="card" style={{ background: "var(--indaco-danger-light)", color: "var(--indaco-danger)", padding: "var(--space-3)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>error</span>
          Errore: {error}
        </div>
      )}

      {data && items.length === 0 && !isLoading && (
        <div className="card text-center text-muted" style={{ padding: "var(--space-10)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 48, display: "block", marginBottom: "var(--space-2)", opacity: 0.4 }}>inbox</span>
          Coda vuota &mdash; nessuna fattura approvata
        </div>
      )}

      {/* Cards fatture */}
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", opacity: isLoading && data ? 0.6 : 1 }}>
        {items.map((item) => {
          const isExpanded = expandedId === item.id;
          return (
            <div key={item.id} className="card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "var(--space-3)" }}>
                {/* Row 1: id chip + fornitore + totale + badge + link detail */}
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-1)" }}>
                  <span className="badge badge-neutral" style={{ fontFamily: "var(--font-family-mono)", flexShrink: 0 }}>
                    {item.id.slice(0, 6)}
                  </span>
                  <span style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--font-size-base)", flex: 1 }}>
                    {item.ragsoc_fornitore || item.ragsoc_filename || "Fornitore sconosciuto"}
                  </span>
                  {item.totale != null && (
                    <span className="badge badge-success">
                      {item.valuta || ""} {item.totale.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                    </span>
                  )}
                  {item.tipo_documento && (
                    <span className={`badge ${item.tipo_documento === "credit_memo" ? "badge-warning" : "badge-info"}`}>
                      {item.tipo_documento === "credit_memo" ? "Nota credito" : "Fattura"}
                    </span>
                  )}
                  <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px", flexShrink: 0 }}
                    title="Apri fattura nella coda di lavoro"
                    onClick={() => navigate(`/rinascente/${item.id}`)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 15, marginRight: 4 }}>open_in_new</span>
                    Fattura
                  </button>
                </div>

                {/* Row 2: griglia dati */}
                <div style={{
                  display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px var(--space-3)",
                  fontSize: "var(--font-size-sm)", marginBottom: "var(--space-2)", marginTop: "var(--space-2)",
                }}>
                  {item.numero_documento && (
                    <>
                      <span className="text-muted">Numero</span>
                      <span>{item.numero_documento}</span>
                    </>
                  )}
                  {item.data_emissione && (
                    <>
                      <span className="text-muted">Data</span>
                      <span>{formatDate(item.data_emissione)}</span>
                    </>
                  )}
                  <span className="text-muted">Righe</span>
                  <span>{item.n_righe}</span>
                  <span className="text-muted">Completeness</span>
                  <span><ConfBadge value={item.completeness} /></span>
                  {item.operator_name && (
                    <>
                      <span className="text-muted">Approvata da</span>
                      <span>{item.operator_name}</span>
                    </>
                  )}
                </div>

                {/* PDF allegato */}
                <div style={{
                  background: "var(--indaco-gray-50)", borderRadius: "var(--border-radius-md)",
                  padding: "var(--space-2)", marginBottom: "var(--space-2)",
                }}>
                  <div className="text-sm text-muted" style={{ marginBottom: "var(--space-1)", fontWeight: "var(--font-weight-medium)" }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}>picture_as_pdf</span>
                    PDF originale {item.pdf_page_count ? `(${item.pdf_page_count} pag.)` : ""}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-gray-400)" }}>picture_as_pdf</span>
                    <span className="text-sm" style={{ flex: 1 }}>
                      {item.pdf_filename || "fattura.pdf"}
                    </span>
                    <button className="btn btn-ghost btn-sm" style={{ padding: "2px 6px" }} title="Scarica PDF"
                      onClick={() => handleDownloadPdf(item)}>
                      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>download</span>
                    </button>
                  </div>
                </div>

                {/* Footer: output_path + approved_at */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-2)" }}>
                  <span style={{ fontFamily: "var(--font-family-mono)", fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-400)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}
                    title={item.output_path || ""}>
                    {item.output_path}
                  </span>
                  {item.approved_at && (
                    <span className="text-muted" style={{ fontSize: "var(--font-size-xs)", whiteSpace: "nowrap" }}>
                      Approvata il {formatDate(item.approved_at)}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: "var(--space-2)", borderTop: "1px solid var(--indaco-gray-100)", paddingTop: "var(--space-2)" }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setExpandedId(isExpanded ? null : item.id)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{isExpanded ? "visibility_off" : "data_object"}</span>
                    {isExpanded ? "Nascondi JSON" : "Vedi JSON"}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => handleDownloadJson(item)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span>
                    Scarica JSON
                  </button>
                  {canWrite && (
                    <button className="btn btn-danger btn-sm" style={{ marginLeft: "auto" }} onClick={() => setConfirmDeleteId(item.id)}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>undo</span>
                      Riapri
                    </button>
                  )}
                </div>
              </div>

              {/* Expanded JSON */}
              {isExpanded && (
                <pre style={{
                  margin: 0, padding: "var(--space-3)",
                  background: "var(--indaco-gray-50)", borderTop: "1px solid var(--indaco-gray-200)",
                  fontSize: "var(--font-size-xs)", overflow: "auto", maxHeight: 400,
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                }}>
                  {JSON.stringify(item.final_data, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>

      {confirmClear && (
        <ConfirmModal
          title="Svuota coda output"
          message={`Vuoi davvero svuotare la coda? Tutte le ${items.length} fatture approvate verranno riaperte (status → pending_review) e i JSON rimossi dal filesystem.`}
          confirmLabel="Svuota"
          danger
          onConfirm={handleClearQueue}
          onCancel={() => setConfirmClear(false)}
        />
      )}
      {confirmDeleteId && (
        <ConfirmModal
          title="Riapri fattura"
          message="Vuoi davvero riaprire questa fattura? Il JSON di output verrà cancellato dal filesystem e la fattura tornerà in stato pending_review."
          confirmLabel="Riapri"
          danger
          onConfirm={() => handleReopen(confirmDeleteId)}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </>
  );
}

// ────────────────────────── HELPERS ──────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function ConfBadge({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const cls = pct >= 85 ? "badge-success" : pct >= 60 ? "badge-warning" : "badge-danger";
  return (
    <span className={`badge ${cls}`} style={{ fontSize: "var(--font-size-xs)" }}>
      {pct}%
    </span>
  );
}
